import '../../Admin.scss';
import '../../../../styles/_table.scss';

import { Table, TableBody, TableCell, TableHead, TableRow } from '@trackman/web-shared-components';
import { ValidSortProp, dataSorter } from 'utilities/dataSorters';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AdminLessonModel } from 'types/admin/lessons';
import { DefaultRoutes } from 'Routes';
import DeleteListItemModal from 'components/Admin/Modals/DeleteListItemModal/DeleteListItemModal';
import HeaderCreateButton from 'components/Admin/HeaderCreateButton/HeaderCreateButton';
import HeaderSearchbar from 'components/Admin/HeaderSearchbar/HeaderSearchbar';
import { ReactComponent as InfoIcon } from 'images/icons/Info.svg';
import { Link } from 'react-router-dom';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as PencilIcon } from 'images/icons/Pencil.svg';
import SortOrderButton from 'components/SortOrderButton/SortOrderButton';
import Tooltip from '@trackman/web-shared-components/components/Tooltip/Tooltip';
import { ReactComponent as TrashIcon } from 'images/icons/Trash.svg';
import dayjs from 'dayjs';
import { getLanguageShortText } from '../../../../utilities/languages';
import { mapBooleanToString } from 'utilities/booleanMapper';
import { observer } from 'mobx-react-lite';
import useSetPageTitle from 'utilities/useSetPageTitle';
import { useStores } from 'index';

type RowsDisplay = {
  id: string;
  title: string;
  description: string;
  languages: string;
  visible: string;
  comingSoon: string;
  certificate: string;
  createdAt: string;
  updatedAt: string;
};

export const LessonList = observer(() => {
  useSetPageTitle({ title: 'Lessons List', subtitle: '' });
  const ls = useStores().lessonEditorStore;
  const [isModalOn, setIsModalOn] = useState(false);
  const [sortByProperty, setSortByProperty] = useState<ValidSortProp<AdminLessonModel>>('title');
  const [sortOrder, setSortOrder] = useState<'ASCENDING' | 'DESCENDING'>('ASCENDING');

  useEffect(() => {
    ls.lessonsList.length === 0 && ls.getLessons();
    setSortByProperty('title');
    setSortOrder('ASCENDING');
  }, [ls]);

  useEffect(() => {
    let filteredList = ls.lessonsList.filter((lesson) => lesson.title.toLowerCase().includes(ls.searchValue.toLowerCase()));
    if (sortByProperty) {
      filteredList = dataSorter(filteredList, sortByProperty, sortOrder);
    }

    ls.filteredList = filteredList;
  }, [ls, ls.searchValue, sortByProperty, sortOrder]);

  const handleSortChange = (property: ValidSortProp<AdminLessonModel>, order: 'ASCENDING' | 'DESCENDING') => {
    setSortByProperty(property);
    setSortOrder(order);
  };

  const rowsDisplay: RowsDisplay[] = useMemo(
    () =>
      ls.filteredList.map((lesson) => {
        return {
          id: lesson.id,
          title: lesson.title.toLowerCase(),
          description: lesson.descriptions.find((description) => description.countryCode === 'en-US')?.text,
          languages: lesson.languages ? lesson.languages.map((lng) => getLanguageShortText(lng)).join(', ') : '',
          visible: mapBooleanToString(lesson.visible),
          comingSoon: mapBooleanToString(lesson.comingSoon),
          certificate: mapBooleanToString(lesson.certification),
          createdAt: dayjs(lesson.createdAt).format('MMM DD, YYYY'),
          updatedAt: dayjs(lesson.updatedAt).format('MMM DD, YYYY'),
        };
      }) as RowsDisplay[],
    [ls.filteredList]
  );

  const deleteLesson = useCallback(() => {
    ls.deleteLessonAndUpdate();
    setIsModalOn(false);
  }, [ls, setIsModalOn]);

  return (
    <div className='admin-content-container'>
      <div className='admin-list-header'>
        <HeaderSearchbar searchValue={ls.searchValue} onChange={ls.setSearchValue} />
        <HeaderCreateButton buttonTitle='New lesson' route={DefaultRoutes.LessonEditor} />
      </div>

      <div className='tmu-table-wrapper'>
        <Table className='tmu-table admin-table'>
          <TableHead>
            <TableRow>
              <TableCell>
                Name
                <SortOrderButton
                  isActive={sortByProperty === 'title'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('title', sortOrder)}
                />
              </TableCell>
              <TableCell>Languages</TableCell>
              <TableCell>
                Visible
                <SortOrderButton
                  isActive={sortByProperty === 'visible'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('visible', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Coming soon
                <SortOrderButton
                  isActive={sortByProperty === 'comingSoon'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('comingSoon', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Certification
                <SortOrderButton
                  isActive={sortByProperty === 'certification'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('certification', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Date created
                <SortOrderButton
                  isActive={sortByProperty === 'createdAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('createdAt', sortOrder)}
                />
              </TableCell>
              <TableCell>
                Last edited
                <SortOrderButton
                  isActive={sortByProperty === 'updatedAt'}
                  sortOrder={sortOrder}
                  onSortChange={(sortOrder) => handleSortChange('updatedAt', sortOrder)}
                />
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsDisplay.length ? (
              rowsDisplay.map((row) => {
                return (
                  <TableRow hover tabIndex={-1} key={row.id}>
                    <TableCell>
                      <div className='description-tooltip'>
                        {row.title}
                        <Tooltip
                          title={row.description ? row.description : 'No description'}
                          placement='bottom'
                          className='table-cell-tooltip'
                        >
                          <InfoIcon />
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell className='languages'>{row.languages}</TableCell>
                    <TableCell>{row.visible}</TableCell>
                    <TableCell>{row.comingSoon}</TableCell>
                    <TableCell>{row.certificate}</TableCell>
                    <TableCell>{row.createdAt}</TableCell>
                    <TableCell>{row.updatedAt}</TableCell>
                    <TableCell>
                      <div className='action-buttons'>
                        <Link to={`/Admin/${DefaultRoutes.LessonEditor}/${row.id}`} className='row-button'>
                          <PencilIcon />
                        </Link>
                        <button
                          className='row-button'
                          onClick={() => {
                            ls.selectedLessonId = row.id;
                            setIsModalOn(true);
                          }}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <Paragraph>
                    <>No lessons found</>
                  </Paragraph>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <DeleteListItemModal isModalOn={isModalOn} setIsModalOn={setIsModalOn} deleteAction={deleteLesson} itemName='lesson' />
    </div>
  );
});

export default LessonList;
